// @ts-nocheck
import React, { useCallback, useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';

import { isValueEmpty } from '../../../../utils/Utils';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import swalToast from '../../swalToast';
import ThreadMessage from './ThreadMessage';
import DeleteModal from '../../Modals/DeleteModal';

const boxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const ChatBoxPanel = ({
  open,
  handleDrawerClose,
  transitionStates = EMPTY_ARRAY,
  chatData,
  userList,
  getAllComments,
  isUserAuthorizedForDeleteNote,
  isUserAuthorizedForUpdateNote,
  attribute,
  editNote,
  entityType,
  entityId,
  deleteNote,
}) => {
  const [selectedThread, setSelectedThread] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedNoteForDelete, setSelectedNoteForDelete] = useState(0);

  const handleChangeSelectedThread = useCallback(
    (thread) => {
      setSelectedThread(thread);
    },
    [setSelectedThread]
  );

  /**
   * @function deleteNoteHandler
   * @returns {void}
   * @param {number} noteId - The id of the note to be deleted.
   * @description This function is used to prepare a note for deletion by specifying its id.
   * It then opens the delete confirmation modal to allow the user to confirm the deletion.
   * @example
   * deleteModel(42);
   */
  const deleteNoteHandler = useCallback(
    (noteId) => {
      setSelectedNoteForDelete(noteId);
      setIsDeleteModalOpen(true);
    },
    [setIsDeleteModalOpen, setSelectedNoteForDelete]
  );

  /**
   * @function handleDeleteNote
   * @param {number} noteId - The id of the note to be deleted.
   * @returns {void}
   * @description Prepares and triggers the deletion of a note by its id.
   * Upon successful deletion, displays a success message, retrieves all comments, and resets user click.
   * @example
   * handleDeleteNote(42);
   */
  const handleDeleteNote = useCallback(
    (noteId) => {
      const params = {
        entity_type: entityType,
        entity_id: entityId,
        note_id: noteId,
      };
      deleteNote(params).then((res) => {
        if (res) {
          swalToast({
            icon: 'success',
            title: 'Note deleted successfully.',
          });
          getAllComments({ entity_id: entityId, entity_type: entityType });

          handleChangeSelectedThread(null);
        }
      });
    },
    [
      handleChangeSelectedThread,
      getAllComments,
      deleteNote,
      entityType,
      entityId,
      swalToast,
    ]
  );

  /**
   * @function closeDeleteModal
   * @returns {void}
   * @description This function is used to  close the delete confirmation modal.
   * @example
   * closeDeleteModal();
   */
  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, [setIsDeleteModalOpen]);

  /**
   * @function deleteConfirm
   * @returns {void}
   * @description once user click on confirm then this function will delete that note.
   * @example
   * deleteConfirm();
   */
  const deleteConfirm = useCallback(() => {
    handleDeleteNote(selectedNoteForDelete);
    closeDeleteModal();
  }, [handleDeleteNote, selectedNoteForDelete, closeDeleteModal]);
  return (
    <div>
      {' '}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        hideBackdrop
        sx={{
          '& .MuiPaper-root': {
            top: '185px',
            bottom: '0px',
            right: '20px',
            width: 558,
          },
        }}
      >
        <Box>
          <Box sx={boxStyles} my={1} px={1}>
            <Box
              sx={{
                ...boxStyles,
                width: '50%',
              }}
            >
              <Typography variant="subtitle1">{`Chat (${attribute?.label})`}</Typography>
            </Box>
            <IconButton onClick={handleDrawerClose} title="Close bot.">
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
          <Divider />
        </Box>
        {/* Thread Messages */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 558,
            pl: 3,
            pr: 1,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '75%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 2,
              position: 'relative',
            }}
          >
            {!isValueEmpty(chatData) ? (
              <Stack spacing={3} width="100%" paddingBottom="200px">
                {chatData?.map((commentData, index) => {
                  return (
                    <ThreadMessage
                      key={commentData?.note_id}
                      commentData={commentData}
                      transitionStates={transitionStates}
                      selectedThread={selectedThread}
                      index={index}
                      userList={userList}
                      handleChangeSelectedThread={handleChangeSelectedThread}
                      deleteNoteHandler={deleteNoteHandler}
                      entityType={entityType}
                      entityId={entityId}
                      editNote={editNote}
                      replyThread={false}
                      getAllComments={getAllComments}
                      disableTagAndTitleFields={false}
                      isUserAuthorizedForDeleteNote={
                        isUserAuthorizedForDeleteNote
                      }
                      isUserAuthorizedForUpdateNote={
                        isUserAuthorizedForUpdateNote
                      }
                    />
                  );
                })}
              </Stack>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '200px',
                }}
              >
                No data present
              </Box>
            )}
          </Box>
        </Box>
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={closeDeleteModal}
          deleteConfirm={deleteConfirm}
          resourceName="Note"
          alertLabelText="Do you want to delete this note?"
        />
      </Drawer>
    </div>
  );
};

export default ChatBoxPanel;

ChatBoxPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleDrawerClose: PropTypes.func.isRequired,
  chatData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  userList: PropTypes.oneOfType([PropTypes.array]).isRequired,
  getAllComments: PropTypes.func.isRequired,
  isUserAuthorizedForDeleteNote: PropTypes.bool.isRequired,
  isUserAuthorizedForUpdateNote: PropTypes.bool.isRequired,
  attribute: PropTypes.oneOfType([PropTypes.object]).isRequired,
  editNote: PropTypes.func.isRequired,
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.number.isRequired,
  deleteNote: PropTypes.func.isRequired,
  transitionStates: PropTypes.arrayOf(PropTypes.string).isRequired,
};
